/**
 * The DSL's vocabulary — what you can type, as data.
 *
 * Two sources, deliberately: QueVee's `/dsl/catalog` is authoritative, because
 * the parser is what decides what exists and a hardcoded list here would drift
 * the day a function is added. But the island cannot fetch (D18), so the host
 * injects it — and until it arrives, {@link DEFAULT_CATALOG} is built from the
 * same constants `expr.ts` already evaluates against, so the reference is never
 * blank and never claims a function the evaluator does not know.
 */

import { JOINED_ONLY_FUNCTIONS, SCALAR_FUNCTIONS, SERIES_FUNCTIONS } from "./expr.js";

/** One parameter, as the picker's expander renders it. */
export interface DslParam {
  name: string;
  kind: "series" | "int" | "number" | "any";
  required: boolean;
  /** Prefilled in the editor, so `rsi` arrives as `rsi(c, 14)`. */
  default?: string | number;
  minimum?: number;
  maximum?: number;
  description?: string;
}

export interface DslFunction {
  name: string;
  /**
   * Reads bar history. Not a restriction — both kinds are legal everywhere —
   * but it decides *where* the column is computed, which is worth showing
   * because it is the difference between instant and a first load.
   */
  series: boolean;

  // Everything below arrives with an injected catalog and is absent from the
  // offline default. The fallback stays deliberately thin: duplicating the
  // prose here would be a second copy of the table to keep in step, and the
  // things that must work with nothing injected — evaluation, placement,
  // completion — need only the name and `series`.

  /** Grouping key for the picker: "Momentum Indicators", "Overlap Studies", … */
  category?: string;
  /** Terse label for a narrow column: `RSI`, `MACD`, `%R`. */
  shortName?: string;
  /** The human sentence, and what search matches on. */
  description?: string;
  params?: DslParam[];
  /** Sibling output names, when this belongs to a multi-output family. */
  outputs?: string[];
  family?: string;
  /** Set when this name is another spelling of its family's first output. */
  aliasOf?: string;
  minArgs?: number;
  maxArgs?: number | null;
}

/** One picker row: an indicator, with its outputs behind the expander. */
export interface DslIndicator {
  name: string;
  category: string;
  shortName: string;
  description: string;
  series: boolean;
  params: DslParam[];
  /** Present only for a multi-output indicator; each entry is a callable name. */
  outputs?: { name: string; shortName: string; description: string }[];
}

export interface DslCatalog {
  /** Every callable name — what completion and highlighting work from. */
  functions: DslFunction[];
  /**
   * One entry per indicator, for the grouped picker. Absent offline: the island
   * cannot fetch (D18), so a host that injects no catalog gets no picker. That
   * is honest — an empty list beats a hardcoded one that drifts the day an
   * indicator is added server-side.
   */
  indicators?: DslIndicator[];
  /** Category order for the picker's sections; server-defined, not alphabetical. */
  categories?: string[];
  /** Typing shorthands: `c` is `close`. Sugar, canonicalised in the AST. */
  fieldAliases: Record<string, string>;
  /** The bar number, 0 = oldest. Synthesised rather than stored. */
  indexField: string;
  /** `mi` is minute and `m` is month — the one pair worth stating outright. */
  frequencyUnits: string[];
  defaultFrequency: string;
}

/** What the island knows on its own, with no compiler reachable. */
export const DEFAULT_CATALOG: DslCatalog = {
  functions: [
    ...SCALAR_FUNCTIONS.map((name) => ({ name, series: false })),
    ...[...SERIES_FUNCTIONS].map((name) => ({ name, series: true })),
  ].sort((a, b) => a.name.localeCompare(b.name)),
  fieldAliases: { o: "open", h: "high", l: "low", c: "close", v: "volume", vol: "volume" },
  indexField: "index",
  frequencyUnits: ["s", "mi", "h", "d", "w", "m", "q", "y"],
  defaultFrequency: "d",
};

/**
 * The injected catalog, narrowed to what a screen can actually answer.
 *
 * `/dsl/catalog` is the whole language, because one registry serves the chart's
 * indicator picker and the screener's expression editor alike. The Symbol
 * Indicators in it are a chart's: they read a second symbol's bars, which is a
 * frame join rather than an expression, and a screener column written with one
 * comes back blank — see `JOINED_ONLY_FUNCTIONS`.
 *
 * Offering a completion that cannot work is worse than offering none. The user
 * has no way to tell the two apart from the list, and the failure arrives later
 * and somewhere else — as an empty column, after a save.
 *
 * **Filtered here rather than at the host.** The island is what knows which
 * expressions a screen can evaluate; the app just fetched some JSON. Every
 * surface that reads a catalog runs it through this, so it does not matter at
 * which level a host injects one.
 *
 * A family loses its excluded *outputs* but keeps the rest, so a future
 * indicator that mixes routable and joined outputs degrades to the half that
 * works instead of vanishing. An indicator left with no callable name at all is
 * dropped, since a picker row that inserts nothing is a dead control.
 */
export function screenerCatalog(catalog: DslCatalog): DslCatalog {
  const usable = (name: string) => !JOINED_ONLY_FUNCTIONS.has(name);
  const indicators = catalog.indicators
    ?.map((indicator) => ({
      ...indicator,
      outputs: indicator.outputs?.filter((output) => usable(output.name)),
    }))
    .filter(
      (indicator) =>
        // Callable under its own name, or through at least one output that is.
        usable(indicator.name) || (indicator.outputs?.length ?? 0) > 0,
    );
  return {
    ...catalog,
    functions: catalog.functions.filter((fn) => usable(fn.name)),
    ...(indicators ? { indicators } : {}),
  };
}

/**
 * Indicators grouped into their categories, in the server's order.
 *
 * The picker's sections come from here rather than from a `groupBy` in the
 * component, so "Category" can become "Alphabetical" without either of them
 * learning what a category is.
 */
export function byCategory(catalog: DslCatalog): { category: string; items: DslIndicator[] }[] {
  const indicators = catalog.indicators ?? [];
  const order = catalog.categories ?? [];
  const groups = new Map<string, DslIndicator[]>();
  for (const item of indicators) {
    const bucket = groups.get(item.category);
    if (bucket) bucket.push(item);
    else groups.set(item.category, [item]);
  }
  // A category the server did not rank sorts last rather than vanishing — the
  // point of a category being a plain string is that one can appear without a
  // client release.
  return [...groups.entries()]
    .map(([category, items]) => ({ category, items }))
    .sort((a, b) => {
      const rank = (c: string) => (order.indexOf(c) < 0 ? order.length : order.indexOf(c));
      return rank(a.category) - rank(b.category) || a.category.localeCompare(b.category);
    });
}

/**
 * Does this indicator match a search? Name, short name and description.
 *
 * The description is in the index because the short name is not enough — the
 * same argument `screener_labels.py` makes for columns. Searching "convergence"
 * has to find MACD, whose label says only "MACD".
 */
export function matchesSearch(item: DslIndicator, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    item.name.toLowerCase().includes(needle) ||
    item.shortName.toLowerCase().includes(needle) ||
    item.description.toLowerCase().includes(needle) ||
    (item.outputs ?? []).some((o) => o.name.toLowerCase().includes(needle))
  );
}

/**
 * Series functions taking only their input — the offline answer to `maxArgs`.
 *
 * Consulted **only** when no catalog was injected. An injected one carries the
 * real arity and wins, so this is not a list the server has to agree with; it is
 * what the fallback knows on its own, and being wrong here means one invented
 * window in an offline host rather than a disagreement about the language.
 *
 * The `pa` family joined late, which is exactly the drift `DEFAULT_CATALOG`'s
 * thinness is meant to avoid: `paSignal` takes an input and nothing else, and
 * without it here completing the name offered `paSignal(c, 20)` — a call the
 * parser rejects, for a window the indicator does not have.
 */
const WINDOWLESS = new Set(["cum", "obv", "pa", "paUpBars", "paDownBars", "paSignal"]);

/**
 * What gets inserted when an entry is clicked.
 *
 * A series function arrives with its window filled in (`sma20`, not `sma`),
 * because that spelling is the one worth teaching: it is shortest, it is what
 * the postfix sugar exists for, and the input defaults to close so there is
 * nothing else to supply. A scalar function gets its parentheses and leaves the
 * caret work to the typist.
 */
/**
 * A completion accepted where there is no receiver, as a **call**.
 *
 * A bare name is a column reference in this language, never a call: `paSignal`
 * compiles to `{kind: "field", field: "paSignal"}` and `sma20` to a field named
 * `sma20`. Only a receiver (`c.paSignal`) or parentheses (`paSignal(c)`) make it
 * a call. So completing to the bare name at the start of an expression produced
 * a column nobody has, and the failure surfaced two steps later as "Unknown
 * column 'paSignal'" — naming the thing the typist picked from a list of
 * functions, which reads as the list being wrong.
 *
 * Only for the no-receiver case. After a dot the bare name is already right, and
 * `c.paSignal(c)` would be nonsense.
 *
 * Returns the caret offset as well as the text, because where it lands is the
 * difference between a call to finish and one to correct: inside empty
 * parentheses for a scalar, and past the whole thing when every argument is
 * already filled in.
 */
export function callInsertionFor(fn: DslFunction): { text: string; caret: number } {
  if (!fn.series) {
    // `abs()`, `round()` — the argument is the point, so the caret goes inside.
    return { text: `${fn.name}()`, caret: fn.name.length + 1 };
  }
  const takesWindow = fn.maxArgs != null ? fn.maxArgs >= 2 : !WINDOWLESS.has(fn.name);
  // `shift` is excluded for `insertionFor`'s reason: a lookback depth is the
  // whole point of the call, so any default is one more thing to delete.
  if (!takesWindow || fn.name === "shift") {
    const text = `${fn.name}(c)`;
    // Inside the parens for `shift`, which still needs its second argument;
    // past them for a one-input series function, which is complete as written.
    return fn.name === "shift"
      ? { text: `${fn.name}(c, )`, caret: fn.name.length + 4 }
      : { text, caret: text.length };
  }
  const window = fn.params?.[1]?.default;
  const text = `${fn.name}(c, ${typeof window === "number" ? window : 20})`;
  return { text, caret: text.length };
}

export function insertionFor(fn: DslFunction): string {
  if (!fn.series) return `${fn.name}()`;
  // A series function with nothing but its input has no window to inline —
  // `obv20` is not `obv(close, 20)`, and the parser says so. The catalog knows
  // the arity, so this asks rather than keeping a list of exceptions in step.
  // Two reasons a series function gets bare parentheses instead of an inlined
  // window, and an injected catalog answers the first on its own:
  //   - it has no window (`obv`, `cum`). `obv20` is not `obv(close, 20)`; the
  //     parser rejects it rather than quietly ignoring the digits;
  //   - it has one, but any value would be a guess (`shift`). A lookback depth
  //     is the whole point of the call, so a default is one more thing to delete.
  const takesWindow = fn.maxArgs != null ? fn.maxArgs >= 2 : !WINDOWLESS.has(fn.name);
  if (!takesWindow || fn.name === "shift") return `${fn.name}(`;
  const window = fn.params?.[1]?.default;
  return typeof window === "number" ? `${fn.name}${window}` : `${fn.name}20`;
}
